import type { DeckConfig, GameState } from "@fiveout/core";
import {
  buildStacks,
  card,
  draws,
  showing,
  TUTORIAL_PLAYER_ID,
  TUTORIAL_PLAYER_NAME,
  tutorialPlayer,
  tutorialRound,
  type Tutorial,
  type TutorialStep,
} from "./script";

/**
 * Tutorial – Basics: the first round a new player sees, and the one the
 * first-launch redirect opens. One player, no house rules: Higher with a +1
 * chain, a losing Lower, a Five Out, an off-by-two Match and a Smash.
 */

/** The default table a new player will meet on the setup screen, so the rules they're taught are the rules they'll play. */
export const BASICS_DECK_CONFIG: DeckConfig = { size: 0.5, wilds: { smash: true, lightning: false, swap: false, peek: false } };

/**
 * Stack indices are 0 center, 1 up, 2 down, 3 left, 4 right (STACK_LAYOUT).
 * Piles are top-first. The left pile starts three deep — an ordinary deal, since
 * cards that don't divide evenly among the players land on the table — and it is
 * what the Smash Card clears at the end.
 */
const BOARD: number[][] = [
  [7], // 0 center — the Five Out
  [12], // 1 up
  [9], // 2 down
  [4, 6, 11], // 3 left — higher, then smashed
  [3], // 4 right — the losing Lower
];

/** Filler under the scripted draws. Never actually played: every step primes what it needs. */
const FILLER = [8, 2, 10, 5, 13, 1, 7, 11, 3, 9, 6, 12, 4, 10, 2, 8, 5, 1, 13, 6, 9, 3, 11, 7];

function initialState(): GameState {
  return tutorialRound({
    id: "tutorial",
    deckConfig: BASICS_DECK_CONFIG,
    stacks: buildStacks("tut", BOARD),
    players: [
      tutorialPlayer(
        TUTORIAL_PLAYER_ID,
        TUTORIAL_PLAYER_NAME,
        FILLER.map((value, i) => card(`tut-deck-${i}`, value, i % 2 === 0 ? "diamonds" : "spades")),
      ),
    ],
  });
}

const STEPS: TutorialStep[] = [
  {
    id: "welcome",
    title: "Welcome to Five Out",
    prompt:
      "**The goal:** play every card in your deck. It's face down, so you never know what's coming.\n\n" +
      "Every card has a number from **1**, the lowest, to **13**, the highest.\n\n" +
      "The next few turns are set up to show you each move.",
    cta: "Deal me in",
  },
  {
    id: "higher",
    title: "Higher or Lower",
    prompt: (s) =>
      "Each turn, pick a pile and guess how your next card compares with its top card: **Higher**, **Lower** or **Match** (the same number).\n\n" +
      `The **left** pile shows a **${showing(s, 3)}**. Tap ▲ to guess Higher.`,
    plays: [
      {
        move: { kind: "guess", stackIndex: 3, guess: "higher" },
        draws: draws("tut-higher", [5, 9]),
        pauses: [
          { on: "card-played", at: "reveal", text: "**A 5.** That's higher than 4, so you guessed right and your card stays." },
          {
            on: "chain-bonus-card",
            title: "A bonus card",
            text:
              "Your 5 was **exactly 1 higher**, so your next card went on the same pile, no guess needed. That's a **bonus card**, and bonus cards always stay.\n\n" +
              "A 9 isn't 1 higher than 5, so the bonus cards stop and your turn ends.",
          },
        ],
      },
    ],
  },
  {
    id: "lower",
    title: "A wrong guess",
    prompt: (s) =>
      `The **right** pile shows a **${showing(s, 4)}**. Cards only go down to 1, so almost every card is higher. Guess Lower anyway, to see what a wrong guess does. Tap ▼.`,
    plays: [
      {
        move: { kind: "guess", stackIndex: 4, guess: "lower" },
        draws: draws("tut-lower", [11, 6]),
        pauses: [
          { on: "card-played", at: "reveal", text: "**An 11.** That isn't lower than 3, so it's a wrong guess." },
          {
            on: "stack-taken",
            text: "A wrong guess means you **pick up the whole pile**, your 11 included. It goes on the **bottom** of your deck.",
          },
          { on: "stack-refill-card", text: "Then your next card went into the empty spot, free, and your turn ended." },
        ],
      },
    ],
  },
  {
    id: "five-out",
    title: "Match",
    prompt: (s) =>
      `**Match** means your card is the same number. The **middle** pile shows a **${showing(s, 0)}**. Tap = to guess Match.`,
    plays: [
      {
        move: { kind: "guess", stackIndex: 0, guess: "match" },
        draws: draws("tut-fiveout", [7, 2, 5, 8, 13]),
        pauses: [
          { on: "card-played", at: "reveal", text: "**A 7!** The same number: an exact Match." },
          {
            on: "five-out-bonus-card",
            nth: -1,
            title: "Five Out",
            text: "**Five Out!** An exact Match puts a bonus card on each of the other four piles: five cards gone in one turn.",
          },
        ],
      },
    ],
  },
  {
    id: "near-miss",
    title: "A close Match",
    prompt: (s) => `The **top** pile shows a **${showing(s, 1)}**. Tap = to guess Match again.`,
    plays: [
      {
        move: { kind: "guess", stackIndex: 1, guess: "match" },
        draws: draws("tut-nearmiss", [4]),
        pauses: [
          {
            on: "card-played",
            at: "reveal",
            text: "**A 4.** Not the same number, but a Match doesn't have to be exact. Off by 2 is close enough: your card stays.",
          },
        ],
      },
    ],
    debrief: "Off by **1** is even better: two bonus cards, on any piles you like. Off by **3 or more** and you pick up the pile.",
    cta: "Next",
  },
  {
    id: "wild",
    title: "Your turn",
    prompt: "Your turn again. Make **any guess** you like on the **left** pile.",
    plays: [
      {
        move: { kind: "guess", stackIndex: 3, guess: "higher", anyGuess: true },
        draws: draws("tut-wild", ["smash", 12]),
        pauses: [
          {
            on: "wild-played",
            at: "showcase",
            title: "A wild card!",
            text: "**You drew a wild card: Smash!**\n\nWild cards have no number, so they're never a wrong guess. Each one does something special instead.",
          },
          { on: "wild-annihilation", title: "Smash", text: "**Smash** removed every card under it from the game, for good." },
          {
            on: "wild-follow-up-card",
            title: "A free card",
            text: "After any wild card, your next card goes on top of it, free. Then your turn ends.",
          },
        ],
      },
    ],
  },
  {
    // Here rather than on the welcome card: by now the player has watched
    // these numbers fall and rise, so the explanation has something to name.
    id: "counts",
    title: "Counting your cards",
    prompt: (s) =>
      "The **number in the corner** of a deck or pile counts its cards. It isn't a card's value.\n\n" +
      `Your deck has **${s.players[0].deck.length}** cards left. Play them all to go out.`,
    cta: "Next",
    pointAt: { deck: true, stacks: [3] },
  },
  {
    id: "done",
    title: "How a round ends",
    prompt:
      "When someone plays their last card, everyone who's had fewer turns gets one more. Then **fewest cards left wins**.\n\n" +
      "There's more: other wild cards, more bonus moves, and extra rules. Three more tutorials under **How to Play** show them.",
    cta: "Play Five Out",
  },
];

export const BASICS: Tutorial = {
  id: "basics",
  title: "Tutorial – Basics",
  initialState,
  steps: STEPS,
};
